import { Chip, Tooltip } from "@mui/material"
import IconCheck from "@mui/icons-material/CheckCircle"
import IconComment from "@mui/icons-material/Comment"
import { blue } from "@mui/material/colors"
import { scaleLinear } from "d3-scale"
import { camelCase, get } from "lodash"
import { FormattedDate, FormattedNumber, FormattedMessage } from "react-intl"
import moment from "moment"
import theme from "../../theme"
import { PropertyTypes } from "~/enums/CustomFields"
import { Bullet } from "../ColorPicker"
import Duration, { formatSecondsInAppropriateUnit } from "../Duration"
import { styleColorById } from "../../theme/status"
import { FormattedDuration } from "../Tickets/FormattedDuration"
import UserName from "../UserName/UserName"
import { TerminationNames } from "../../enums/TerminationTypes"
import SplittedIcon from "../Icons/SplittedIcon"
import FormattedDateTimeNumeric from "../FormattedDateTime/FormattedDateTimeNumeric"

export const getStyleById = id => ({
  backgroundColor: styleColorById(id),
})

const dateProps = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
}

const checkboxEnabled = {
  color: blue[500],
  verticalAlign: "middle",
}
const checkboxDisabled = {
  color: theme.palette.grey[400],
  verticalAlign: "middle",
}

export const EnumCell =
  (Formatter, colorByValue = getStyleById) =>
  ({ value, ...props }) =>
    (
      <Chip
        sx={{ "& .MuiChip-label": { textOverflow: "initial" } }}
        label={<Formatter value={value} {...props} />}
        style={colorByValue(value, props)}
      />
    )

export const BooleanCell = ({ value }) => <IconCheck style={value ? checkboxEnabled : checkboxDisabled} />
export const BooleanCellWithExtraValue = accessor => props => {
  const {
    value,
    row: { original },
  } = props
  return (
    <>
      <IconCheck style={value ? checkboxEnabled : checkboxDisabled} />
      {original[accessor] ? ` (${original[accessor]})` : null}
    </>
  )
}

export const ColorCell = ({ value }) => (value ? <Bullet color={value} /> : null)
export const IconCell =
  (IconComponent, props) =>
  ({ value }) =>
    value ? <IconComponent {...props} /> : null
export const CommentCell = ({ value }) =>
  (value && value.length && (
    <Tooltip placement="top" title={value}>
      <IconComment color="primary" />
    </Tooltip>
  )) ||
  null

export const DifferenceCell = EnumCell(
  ({ value }) => <FormattedNumber value={value} maximumFractionDigits={0} minimumIntegerDigits={1} />,
  value => ({
    color: "white",
    background: scaleLinear()
      .domain([9, 10, 10, 30, 30, 30])
      .range(["#66b032", "#66b032", "#FB9902", "#FB9902", "#FE2712", "#FE2712"])(Math.round(value)),
  })
)

export const CustomCell = ({ propertyType, defaultValue }) => {
  switch (propertyType) {
    case PropertyTypes.DATE:
      return <DateCell value={defaultValue} />

    case PropertyTypes.NUMBER:
      return <DecimalCell value={defaultValue} />
    default:
      return defaultValue
  }
}

export const DurationCell = ({ value }) => (value && <FormattedDuration value={value} />) || null
export const DateCell = ({ value, ...props }) =>
  value ? <FormattedDate value={value} {...dateProps} {...props} /> : null
export const IntegerCell = ({ value }) => <FormattedNumber value={value} />
export const InvertedBoolCell = ({ value }) => <IconCheck style={!value ? checkboxEnabled : checkboxDisabled} />
export const DecimalCell = ({ value }) => <FormattedNumber value={value} maximumFractionDigits={2} />

export const DateTimeCell = ({ value }) => {
  if (!value) return null
  return <FormattedDateTimeNumeric date={value} />
}

export const ReadableSecondsCell = ({ value }) => <Duration duration={formatSecondsInAppropriateUnit(value)} />

export const TerminationCell = ({ value }) => <FormattedMessage id={`type.termination.${TerminationNames[value]}`} />

export const ActionCell = ({ value }) => <FormattedMessage id={`action.${value}`} />

export const SplittedIconCell = ({ value: { splitted, isSplit } }) => (
  <SplittedIcon splitted={splitted} isSplit={isSplit} style={{ verticalAlign: "middle" }} />
)

export const TranslatedTerm = ({ value }) => <FormattedMessage id={`term.${camelCase(value)}`} />

// Generic cell renderer for any data type - Distinguishes between dates, boolean-like strings, or falls back to raw value
export const HistoryGridCell = props => {
  const { value } = props
  // Check if value matches YYYY-MM-DD format (e.g., 2025-12-24)
  if (moment(value, "YYYY-MM-DD", true).isValid()) {
    return DateCell(props)
  }

  if (["True", "False"].includes(value)) {
    return <FormattedMessage id={`common.${camelCase(value)}`} />
  }

  return value
}

export const GridUser = ({ value }) => <UserName user={value} />

export const SimpleGridUser = accessor => props => {
  const user = get(props, `row.original.${accessor}`)
  return <UserName user={user} />
}
